// The media stage + Videos/Images browser, shared by the Discover quick-look
// (ProjectQuickLook) and the project page's Media tab.
import { useEffect, useState } from "react";
import { resolveEmbedData } from "../../components/VideoModal";
import ImageWithFallback from "../../components/ImageWithFallback";
import {
  extractYoutubePlaylistId,
  fetchYoutubePlaylistVideoIds,
  fetchYoutubeTitles,
  youtubeThumbnailUrl,
} from "../../lib/youtubePlaylist";
import "./MediaBrowser.css";

const TABS = [
  { group: "videos", label: "Videos" },
  { group: "images", label: "Images" },
];

export function youtubeVideoId(url) {
  const data = resolveEmbedData(url, "demo");
  if (data.kind !== "youtube-video") return null;
  return data.embedUrl.match(/\/embed\/([^?]+)/)?.[1] || null;
}

function videoThumb(url) {
  const id = youtubeVideoId(url);
  if (id) return youtubeThumbnailUrl(id);
  // Drive serves a poster frame for video files at the same thumbnail endpoints
  // it uses for images, so the raw url passes straight through ImageWithFallback.
  if (resolveEmbedData(url, "demo").kind === "drive-file") return url;
  return null;
}

/**
 * The stage iframe's src. The first video autoplays when the page opens, so it
 * starts muted (a page shouldn't blare sound unasked); once the visitor picks a
 * video themselves it plays with sound. `mute` is YouTube's embed param — Drive
 * previews don't autoplay, so they need nothing.
 *
 * Measured in Chrome: with `mute=1` the <video> is muted before it ever plays.
 * YouTube's speaker icon flickers "on" for ~0.3s at start, then settles on
 * muted — cosmetic only. Don't swap this for IFrame-API mute()+playVideo():
 * that is just as silent but leaves the icon stuck on "on" for good.
 */
export function stageSrc(url, muted) {
  const data = resolveEmbedData(url, "demo");
  const src = data.embedUrl || "";
  return muted && data.kind?.startsWith("youtube") ? `${src}&mute=1` : src;
}

function toUrl(url) {
  return new URL(/^https?:\/\//i.test(url) ? url : `https://${url}`);
}

// Drive file names live behind an opaque id and can't be fetched without a key, so
// Drive items keep their section label. This catches everything Drive-hosted.
function isDriveUrl(url) {
  try {
    const h = toUrl(url).hostname.toLowerCase();
    return (
      h.includes("drive.google.com") ||
      h.includes("docs.google.com") ||
      h.includes("googleusercontent.com")
    );
  } catch {
    return false;
  }
}

// S3 and other direct links carry the real filename as the last path segment.
// ".../clips/Final%20Cut.mp4" -> "Final Cut". No request needed.
function nameFromUrl(url) {
  try {
    const last = decodeURIComponent(toUrl(url).pathname.split("/").filter(Boolean).pop() || "");
    const base = last.replace(/\.[a-z0-9]{1,5}$/i, "").replace(/[._-]+/g, " ").trim();
    return base || null;
  } catch {
    return null;
  }
}

/**
 * The real name to show for a row, or null to fall back to the section label:
 * YouTube -> the video's oEmbed title (keyed by video id), S3 / any direct link ->
 * its filename, Drive -> null.
 */
function resolveName(url, videoId, titles) {
  if (videoId) return titles[videoId] || null;
  if (isDriveUrl(url)) return null;
  return nameFromUrl(url);
}

/**
 * One row per URL — a project with eight renders should list eight, not one.
 * A YouTube playlist link expands into one row per video once `playlists`
 * (url -> video ids, filled in by the caller) resolves; until then it shows as
 * a single loading row. `titles` (video id -> oEmbed title) upgrades a row from
 * its section label to the source's real name as those requests land.
 */
export function toItems(media, playlists = {}, titles = {}) {
  const raw = media.flatMap((m) =>
    m.urls.flatMap((url) => {
      const isVideo = m.group === "videos";
      const playlistId = isVideo ? extractYoutubePlaylistId(url) : null;

      if (playlistId) {
        const videoIds = playlists[url];
        if (!videoIds) return [{ url, group: m.group, section: m.label, thumb: null, loading: true }];

        return videoIds.map((videoId) => ({
          url: `https://youtu.be/${videoId}`,
          group: m.group,
          section: m.label,
          thumb: youtubeThumbnailUrl(videoId),
          name: titles[videoId] || null,
        }));
      }

      const videoId = isVideo ? youtubeVideoId(url) : null;
      return [
        {
          url,
          group: m.group,
          section: m.label,
          thumb: isVideo ? videoThumb(url) : null,
          name: resolveName(url, videoId, titles),
        },
      ];
    })
  );

  // Number rows within a section only once it turns out to hold more than one.
  const counts = {};
  raw.forEach((it) => (counts[it.section] = (counts[it.section] || 0) + 1));
  const seen = {};
  return raw.map((it) => {
    seen[it.section] = (seen[it.section] || 0) + 1;
    const fallback = it.loading
      ? `${it.section} (loading…)`
      : counts[it.section] > 1
      ? `${it.section} ${seen[it.section]}`
      : it.section;
    // A resolved source name wins; otherwise the numbered section label stands in.
    return { ...it, title: it.name || fallback };
  });
}

/** Run-length grouping: items already arrive in MEDIA_KINDS order, so runs are sections. */
function toSections(items) {
  const sections = [];
  for (const item of items) {
    const last = sections[sections.length - 1];
    if (last?.label === item.section) last.items.push(item);
    else sections.push({ label: item.section, items: [item] });
  }
  return sections;
}

export default function MediaBrowser({ project }) {
  const [playlists, setPlaylists] = useState({});
  const [titles, setTitles] = useState({});
  const items = toItems(project.media, playlists, titles);

  // ponytail: seeded once from props. Callers key us by project id, so a
  // different project remounts rather than reusing this state.
  const [active, setActive] = useState(items[0] || null);
  const [tab, setTab] = useState(items[0]?.group || "videos");
  const [picked, setPicked] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const playlistUrls = project.media
      .filter((m) => m.group === "videos")
      .flatMap((m) => m.urls)
      .filter((url) => extractYoutubePlaylistId(url));

    playlistUrls.forEach((url) => {
      fetchYoutubePlaylistVideoIds(extractYoutubePlaylistId(url)).then((ids) => {
        if (!cancelled) setPlaylists((prev) => ({ ...prev, [url]: ids }));
      });
    });

    return () => {
      cancelled = true;
    };
  }, [project.media]);

  // Real YouTube names via oEmbed, for standalone videos and the ones a playlist
  // expands into. Drive/S3 names are derived from the URL in toItems and need no
  // request. Empty-string entries mark ids we've already tried (private/deleted
  // videos have no oEmbed title), so a failed lookup isn't retried every render.
  useEffect(() => {
    const single = project.media
      .filter((m) => m.group === "videos")
      .flatMap((m) => m.urls)
      .map(youtubeVideoId)
      .filter(Boolean);

    const fromPlaylists = Object.values(playlists).flat();

    const wanted = [...new Set([...single, ...fromPlaylists])].filter((id) => !(id in titles));
    if (!wanted.length) return;

    let cancelled = false;
    fetchYoutubeTitles(wanted).then((map) => {
      if (cancelled) return;
      const merged = Object.fromEntries(wanted.map((id) => [id, map[id] || ""]));
      setTitles((prev) => ({ ...prev, ...merged }));
    });

    return () => {
      cancelled = true;
    };
  }, [project.media, playlists, titles]);

  const tabs = TABS.filter((t) => items.some((i) => i.group === t.group));
  const sections = toSections(items.filter((i) => i.group === tab));

  return (
    <div className="pv-body">
      <div className="pv-stage">
        {!active ? (
          <p className="pv-nomedia">No media for this project yet.</p>
        ) : active.group === "images" ? (
          <ImageWithFallback
            src={active.url}
            alt={`${project.name} — ${active.title}`}
          />
        ) : (
          // "demo" makes resolveEmbedData sniff Drive-then-YouTube, which is what we want
          // for every video kind: the URL may be either, and the sheet never says which.
          <iframe
            title={`${project.name} — ${active.title}`}
            src={stageSrc(active.url, !picked)}
            allow="autoplay; fullscreen"
            allowFullScreen
          />
        )}
      </div>

      <aside className="pv-browser">
        <div className="pv-browserInner">
          <div className="pv-tabs">
            {tabs.map((t) => (
              <button
                key={t.group}
                type="button"
                className={t.group === tab ? "pv-tab pv-tab--on" : "pv-tab"}
                onClick={() => setTab(t.group)}
              >
                {t.label}
              </button>
            ))}
          </div>

          <div className="pv-list">
            {sections.map((s) => (
              <section key={s.label}>
                <h4 className="pv-sectionHead">{s.label}</h4>

                {s.items.map((item, i) => (
                  <button
                    key={`${item.url}-${i}`}
                    type="button"
                    className="pv-item"
                    aria-current={item.url === active?.url ? "true" : undefined}
                    onClick={() => {
                      setActive(item);
                      setPicked(true);
                    }}
                  >
                    <span className="pv-thumb">
                      {item.group === "images" ? (
                        <ImageWithFallback src={item.url} alt="" />
                      ) : item.thumb ? (
                        <>
                          <ImageWithFallback src={item.thumb} alt="" />
                          <span className="pv-playBadge" aria-hidden="true">▶</span>
                        </>
                      ) : (
                        <span className="pv-play">▶</span>
                      )}
                    </span>

                    <span className="pv-itemTitle">{item.title}</span>
                  </button>
                ))}
              </section>
            ))}
          </div>
        </div>
      </aside>
    </div>
  );
}
